import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import BASE_URL from "../api";
import { AppDispatch, RootState } from "../../../store/store";
import { AuthResponse, AuthState, ChangePasswordPayload, ChangePasswordResponse, ForgotPasswordPayload, ResetPasswordPayload, User } from "../types/auth";
import { PersistPartial } from "redux-persist/es/persistReducer";
import { AdminState } from "./adminSlice";
import { BookingState } from "./bookingSlice";
import { CommentState } from "./propertyCommentSlice";
import { PropertyState } from "./propertySlice";

export interface LoginRequest {
  email: string;
  password: string;
}

interface UsersState {
  users: User[];
  usersLoading: boolean;
  usersError: any;
}

// types.ts
export interface RegisterPayloadOld {
  last_name: string;
  first_name: string;
  email: string;
  phone: string;
  password: string;
  password_confirmation: string;
  role_id: number; // optional, default to 2 for regular users
  profile_picture: File | null;
}
export interface RegisterPayload {
  last_name: string;
  first_name: string;
  email: string;
  phone: string;
  password: string;
  password_confirmation: string;
  role_id: number;

  profile_picture: File | null;

  username?: string;
  gender?: string;
  identity_type?: string;
  bvn?: string | null;
  nin?: string | null;
  address?: string;
  country?: string;
  state?: string;
}

const storedUser = localStorage.getItem("user");
const storedToken = localStorage.getItem("access_token");

const initialState: AuthState & {
  success: string | null;
  isAuthenticated: boolean
  justLoggedIn: boolean;

  users: User[];
  lawyers: User[];
  usersLoading: boolean;
  usersError: any;
} = {
  user: storedUser ? JSON.parse(storedUser) : null,
  // access_token: localStorage.getItem("access_token") || null,
  access_token: storedToken,
  loading: false,
  error: null,
  success: null,
  isAuthenticated: !!storedToken,
  // isAuthenticated: !!localStorage.getItem("access_token"),
  justLoggedIn: false,

  users: [],
  lawyers: [],
  usersLoading: false,
  usersError: null,
};

/* =======================
   API HELPER
======================= */

async function api<T>(
  path: string,
  method: "GET" | "POST" | "PUT" | "DELETE",
  token?: string,
  body?: unknown
): Promise<T> {
  let res: Response;

  try {
    res = await fetch(`${BASE_URL}${path}`, {
      method,
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
        ...(token && { Authorization: `Bearer ${token}` }),
      },
      body: body ? JSON.stringify(body) : undefined,
    });
  } catch {
    throw new Error("Server error. Unable to reach the server.");
  }

  // TOKEN EXPIRED
  // if (res.status === 401) {

  //   localStorage.removeItem("persist:root");
  //   localStorage.removeItem("access_token");

  //   window.location.href = "/login";

  //   throw new Error("Session expired");
  // }

  let data: any = null;

  try {
    data = await res.json();
  } catch {
    data = null;
  }

  if (!res.ok) {
    const validationMessage = Object.values(data?.errors || {})
      .flat()
      .find(Boolean);

    const message =
      data?.message ||
      data?.error ||
      validationMessage ||
      (res.status === 401
        ? "Invalid credentials"
        : res.status >= 500
        ? "Server error. Please try again later."
        : "Something went wrong. Please try again.");

    throw new Error(String(message));
  }

  return data as T;
}

/* =======================
   SLICE
======================= */

const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    start(state) {
      state.loading = true;
      state.error = null;
      state.success = null;
    },

    fail(state, action: PayloadAction<any>) {
      state.loading = false;

      // Laravel validation errors
      if (action.payload?.errors) {
        state.error = action.payload.errors;
      } else {
        state.error = action.payload?.message || "Something went wrong";
      }
    },
    registerSuccess(
      state,
      action: PayloadAction<{ user: User; message: string }>
    ) {
      state.loading = false;

      // ❌ DO NOT authenticate
      state.user = null;
      state.access_token = null;
      state.isAuthenticated = false;
      state.justLoggedIn = false;

      state.success = action.payload.message;
      state.error = null;

      // ❌ NEVER persist on register
      localStorage.removeItem("access_token");
      localStorage.removeItem("user");
    },

    authSuccess(
      state,
      action: PayloadAction<{
        user: User;
        access_token: string;
        message: string;
      }>
    ) {
      state.loading = false;

      const rawUser = action.payload.user;

      state.user = {
        ...rawUser,
        image: rawUser.image_url || rawUser.image,
      };

      state.access_token = action.payload.access_token;
      state.isAuthenticated = true;
      state.justLoggedIn = true;
      state.success = action.payload.message;
      state.error = null;

      localStorage.setItem("access_token", action.payload.access_token);
      localStorage.setItem("user", JSON.stringify(state.user)); // 👈 important
    },

    setUser(state, action: PayloadAction<User>) {
      const rawUser = action.payload;

      state.user = {
        ...rawUser,
        image: rawUser.image_url || rawUser.image,

        //  FORCE SAFE DEFAULT
        kyc: rawUser.kyc

      };

      state.isAuthenticated = true;
      state.loading = false;
      state.error = null;
    },

    setLawyers(state, action: PayloadAction<User[]>) {
      state.lawyers = action.payload;
    },


    updateUser(state, action: PayloadAction<User>) {
      state.user = action.payload;
      state.loading = false;
    },

    clearAuthFeedback(state) {
      state.error = null;
      state.success = null;
      state.justLoggedIn = false;
    },

    verifyOtpSuccess(state, action: PayloadAction<string>) {
      state.loading = false;
      state.error = null;
      state.success = action.payload;
    },

    forgotPasswordSuccess(state, action: PayloadAction<string>) {
      state.loading = false;
      state.success = action.payload;
      state.error = null;
    },

    resetPasswordSuccess(state, action: PayloadAction<string>) {
      state.loading = false;
      state.success = action.payload;
      state.error = null;
    },

    logout(state) {
      state.user = null;
      state.access_token = null;
      state.isAuthenticated = false;
      state.justLoggedIn = false;
      state.loading = false;
      state.error = null;
      state.success = null;

      localStorage.removeItem("access_token");
      localStorage.removeItem("persist:root");
      localStorage.removeItem("user");
      localStorage.clear();
    },

    changePasswordSuccess(state, action: PayloadAction<string>) {
      state.loading = false;
      state.success = action.payload;
      state.error = null;
    },

    changePasswordFail(state, action: PayloadAction<any>) {
      state.loading = false;
      state.error = action.payload;
    },

    fetchUsersStart(state) {
      state.usersLoading = true;
      state.usersError = null;
    },

    fetchUsersSuccess(state, action: PayloadAction<User[]>) {
      state.usersLoading = false;
      state.users = action.payload;
    },

    fetchUsersFail(state, action: PayloadAction<any>) {
      state.usersLoading = false;
      state.usersError = action.payload;
    },
    activateUserStart(state) {
      state.usersLoading = true;
      state.usersError = null;
    },
    activateUserSuccess(state, action: PayloadAction<User>) {
      state.usersLoading = false;
      const index = state.users.findIndex(u => u.id === action.payload.id);
      if (index !== -1) state.users[index] = action.payload;
    },
    activateUserFail(state, action: PayloadAction<any>) {
      state.usersLoading = false;
      state.usersError = action.payload;
    },

    deactivateUserStart(state) {
      state.usersLoading = true;
      state.usersError = null;
    },
    deactivateUserSuccess(state, action: PayloadAction<User>) {
      state.usersLoading = false;
      const index = state.users.findIndex(u => u.id === action.payload.id);
      if (index !== -1) state.users[index] = action.payload;
    },
    deactivateUserFail(state, action: PayloadAction<any>) {
      state.usersLoading = false;
      state.usersError = action.payload;
    },

  },
});

export const {
  start,
  fail,
  registerSuccess,
  authSuccess,
  setUser,
  updateUser,
  clearAuthFeedback,
  verifyOtpSuccess,
  forgotPasswordSuccess,
  resetPasswordSuccess,
  logout,
  changePasswordSuccess,
  changePasswordFail,
  setLawyers,
  fetchUsersStart,
  fetchUsersSuccess,
  fetchUsersFail,
  activateUserStart,
  activateUserSuccess,
  activateUserFail,
} = authSlice.actions;

export default authSlice.reducer;

/* =======================
   THUNKS
======================= */

export const fetchUsersByRole =
  (role: string) =>
    async (dispatch: AppDispatch, getState: () => RootState) => {

      dispatch(fetchUsersStart());

      try {

        const token = getState().auth.access_token;

        if (!token) {
          throw new Error("Unauthorized");
        }

        const res = await api<{
          success: boolean;
          data: User[];
        }>(
          `/user/users?role=${role}`,
          "GET",
          token
        );

        dispatch(fetchUsersSuccess(res.data));

        return res.data;

      } catch (err: any) {

        dispatch(fetchUsersFail(err.message || "Failed to fetch users"));

        return [];
      }
    };


export const register =
  (body: RegisterPayload) =>
    async (dispatch: AppDispatch) => {
      dispatch(start());

      try {
        const formData = new FormData();

        Object.entries(body).forEach(([key, value]) => {
          if (value !== null) {
            formData.append(key, value as any);
          }
        });

        const res = await fetch(`${BASE_URL}/auth/register`, {
          method: "POST",
          body: formData, // ✅ NO JSON
        });

        const data = await res.json();

        if (!res.ok) throw data;

        // Backend envelopes differ (sometimes { user }, sometimes { data }),
        // so derive the email from whichever shape came back.
        const email =
          data?.email ||
          data?.user?.email ||
          data?.data?.user?.email ||
          data?.data?.email ||
          body.email;

        if (email) {
          localStorage.setItem("pending_email", email);
        }

        dispatch(
          registerSuccess({
            user: data?.user ?? ({} as User),
            message: data?.message || "Account created successfully 🎉",
          })
        );

        // A 2xx response means the account exists; callers must not rely on
        // backend-specific flags to decide whether registration succeeded.
        return {
          ...(data && typeof data === "object" ? data : {}),
          status: data?.status ?? true,
          requires_otp: data?.requires_otp ?? true,
          email,
        };
      } catch (err: any) {
        dispatch(fail(err));
        throw err; // re-throw for component-level handling
      }
    };

export const fetchLawyers =
  () =>
    async (
      dispatch: AppDispatch,
      getState: () => RootState
    ) => {

      dispatch(start());

      try {

        const token = getState().auth.access_token;

        if (!token) {
          throw new Error("Unauthorized");
        }


        const data = await api<{
          data: User[];
        }>(
          "/user/lawyers",
          "GET",
          token
        );

        dispatch(setLawyers(data.data));

      } catch (err: any) {

        dispatch(fail(err));
        throw err;

      }
    };

export const registerBuyer =
  (body: RegisterPayload) =>
    async (dispatch: AppDispatch, getState: () => RootState) => {
      if (getState().auth.loading) return;

      dispatch(start());

      try {
        const formData = new FormData();

        Object.entries(body).forEach(([key, value]) => {
          if (value !== null && value !== undefined) {
            formData.append(key, value as any);
          }
        });

        const res = await fetch(`${BASE_URL}/auth/register_buyer`, {
          method: "POST",
          body: formData,
        });

        const data = await res.json();

        if (!res.ok) throw data;

        dispatch(
          registerSuccess({
            user: data.user,
            message: data.message || "Buyer registered successfully 🎉",
          })
        );

        return data;
      } catch (err: any) {
        dispatch(fail(err));
        throw err;
      }
    };
export const verifyOtp =
  (body: { email: string; otp: string }) =>
    async (dispatch: AppDispatch, getState: () => RootState) => {
      dispatch(start());

      try {
        const data = await api<{ message: string }>(
          "/auth/verify-otp",
          "POST",
          undefined,
          body
        );

        const message = data.message || "Email verified successfully.";

        if (getState().auth.access_token) {
          await dispatch(fetchMe());
          dispatch(verifyOtpSuccess(message));
        } else {
          dispatch(
            authSuccess({
              user: {} as User, // optional, or refetch /me
              access_token: "",
              message,
            })
          );
        }

        return data;
      } catch (err: any) {
        dispatch(fail(err));
        throw err;
      }
    };

export const login =
  (body: LoginRequest) =>
    async (dispatch: AppDispatch) => {
      dispatch(start());

      try {
        const data = await api<AuthResponse>(
          "/auth/login",
          "POST",
          undefined,
          body
        );

        dispatch(
          authSuccess({
            user: data.user,
            access_token: data.access_token,
            message: "Login successful ✅",
          })
        );

        return data;
      } catch (err: any) {
        dispatch(fail(err));
        return;
      }
    };

export const resendOtp =
  (payload: { email: string }) =>
    async (dispatch: AppDispatch) => {
      try {
        dispatch(start());

        const res = await fetch(`${BASE_URL}/auth/resend-otp`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(payload),
        });

        const data = await res.json();

        if (!res.ok) throw data;

        dispatch(clearAuthFeedback(data.message));

        return data;
      } catch (err: any) {
        dispatch(fail(err));
        throw err;
      }
    };

export const forgotPassword =
  (body: ForgotPasswordPayload) =>
    async (dispatch: AppDispatch) => {
      dispatch(start());

      try {
        const data = await api<{ status: boolean; message?: string; otp?: string }>(
          "/auth/forgot-password",
          "POST",
          undefined,
          body
        );

        dispatch(
          forgotPasswordSuccess(
            data.message || "OTP sent to your email"
          )
        );

        return data;
      } catch (err: any) {
        dispatch(fail(err));
        throw err;
      }
    };

export const resetPassword =
  (body: ResetPasswordPayload) =>
    async (dispatch: AppDispatch) => {
      dispatch(start());

      try {
        const data = await api<{ status: boolean; message: string }>(
          "/auth/reset-password",
          "POST",
          undefined,
          body
        );

        dispatch(resetPasswordSuccess(data.message));

        return data;
      } catch (err: any) {
        dispatch(fail(err));
        throw err;
      }
    };

export const changePassword =
  (body: ChangePasswordPayload) =>
    async (dispatch: AppDispatch, getState: () => RootState) => {
      dispatch(start());

      try {
        const token = getState().auth.access_token;
        if (!token) throw new Error("Unauthorized");

        const data = await api<ChangePasswordResponse>(
          "/user/change-password", // make sure this matches backend
          "POST",
          token,
          body
        );

        dispatch(changePasswordSuccess(data.message));
        return data;

      } catch (err: any) {

        // EXTRACT REAL ERROR FROM LARAVEL
        const message =
          err?.response?.data?.message || // Laravel message
          err?.response?.data?.errors?.old_password?.[0] || // validation
          err?.response?.data?.errors?.new_password?.[0] ||
          err.message ||
          "Something went wrong";

        dispatch(changePasswordFail(message));

        throw new Error(message); //  pass clean message to UI
      }
    };

export const updateProfile =
  (body: FormData | Record<string, any>) =>
    async (dispatch: AppDispatch, getState: () => RootState) => {
      dispatch(start());

      try {
        const token = getState().auth.access_token;
        if (!token) throw new Error("Unauthorized");

        const isFormData = body instanceof FormData;

        const res = await fetch(`${BASE_URL}/user/update-profile`, {
          method: "POST", //  IMPORTANT (Laravel handles file better with POST)
          headers: {
            ...(isFormData ? {} : { "Content-Type": "application/json" }),
            Authorization: `Bearer ${token}`,
            Accept: "application/json",
          },
          body: isFormData ? body : JSON.stringify(body),
        });

        const data = await res.json();

        if (!res.ok) {
          const message =
            data?.message ||
            Object.values(data?.errors || {})?.flat()?.[0] ||
            "Update failed";

          throw new Error(message);
        }

        dispatch(updateUser(data.user));
        localStorage.setItem("user", JSON.stringify(data.user));

        return data;
      } catch (err: any) {
        dispatch(fail({ message: err.message }));
        throw err;
      }
    };


export const fetchMe =
  () =>
    async (dispatch: AppDispatch, getState: () => RootState) => {
      dispatch(start());

      try {
        const token = getState().auth.access_token;
        if (!token) throw new Error("Unauthorized");

        const data = await api<{
          user?: User;
          data?: User | { user?: User };
        }>("/user/me", "GET", token);

        const rawUser =
          data.user ??
          (data.data && "user" in data.data
            ? (data.data as { user?: User }).user
            : (data.data as User)) ??
          (data as unknown as User);

        if (!rawUser || typeof rawUser !== "object") {
          throw new Error("Unable to load your profile.");
        }

        const user = {
          ...rawUser,
          kyc: rawUser.kyc ?? null,
        };

        dispatch(setUser(user));
        localStorage.setItem("user", JSON.stringify(user));

        return user;
      } catch (err: any) {
        dispatch(fail(err.message));
      }
    };

export const updateKyc =
  (body: FormData | Record<string, any>) =>
    async (dispatch: AppDispatch, getState: () => RootState) => {
      dispatch(start());

      try {
        const token = getState().auth.access_token;
        if (!token) throw new Error("Unauthorized");

        const isFormData = body instanceof FormData;

        const res = await fetch(`${BASE_URL}/user/kyc/update`, {
          method: "POST", // ✅ FIXED
          headers: {
            ...(isFormData ? {} : { "Content-Type": "application/json" }),
            Authorization: `Bearer ${token}`,
            Accept: "application/json", // ✅ IMPORTANT for Laravel
          },
          body: isFormData ? body : JSON.stringify(body),
        });

        let data: any;

        try {
          data = await res.json();
        } catch {
          throw new Error("Invalid server response");
        }

        if (!res.ok) {
          // ✅ FULL ERROR EXTRACTION
          const message =
            data?.message ||
            (data?.errors &&
              Object.values(data.errors).flat().join(", ")) ||
            JSON.stringify(data) ||
            "Request failed";

          throw new Error(message);
        }

        dispatch(
          updateUser({
            ...getState().auth.user,
            kyc: data.data,
            // kyc: structuredClone(data.data), 
          } as any)
        );

        dispatch(clearAuthFeedback());

        return data;
      } catch (err: any) {


        dispatch(
          fail({
            message: err.message,
          })
        );

        throw err;
      }
    };


export const logoutUser =
  () => async (dispatch: AppDispatch) => {
    localStorage.removeItem("access_token");
    dispatch(logout());
  };


export type { AuthState };


